import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useCognitoAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import type { BeangoError } from '@/lib/beango-error';
import { cognitoAuth } from '@/lib/cognito';
import { ErrorCode, userMessage } from '@/lib/error-codes';

interface Profile {
  id: number;
  auth_user_id: string;
  email: string;
  full_name?: string;
  avatar_url?: string;
  organization_id?: number;
  created_at?: string;
  updated_at?: string;
}

export function UserSettings() {
  const navigate = useNavigate();
  const { idToken } = useCognitoAuth();

  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Display name editing
  const [displayName, setDisplayName] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [nameSaved, setNameSaved] = useState(false);

  // Email change
  const [newEmail, setNewEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailMessage, setEmailMessage] = useState<string | null>(null);
  const [emailSending, setEmailSending] = useState(false);
  const [emailPending, setEmailPending] = useState(false);

  // Password change
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await api.get<Profile>('/api/profiles/me', { errorCode: ErrorCode.FETCH_PROFILE, token: idToken! });
      setProfile(resp);
      setDisplayName(resp.full_name ?? '');
    } catch (err) {
      setError(err instanceof Error && 'code' in err ? userMessage((err as BeangoError).code) : 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, [idToken]);

  useEffect(() => {
    if (!idToken) return;
    void fetchProfile();
  }, [fetchProfile, idToken]);

  const handleSaveName = useCallback(async () => {
    if (!displayName.trim()) {
      setNameError('Display name cannot be empty.');
      return;
    }
    if (displayName.length > 100) {
      setNameError('Display name cannot exceed 100 characters.');
      return;
    }
    setNameError(null);
    setNameSaved(false);
    setSaving(true);
    try {
      const updated = await api.patch<Profile>('/api/profiles/me', {
        full_name: displayName.trim(),
      }, { errorCode: ErrorCode.SAVE_DISPLAY_NAME, token: idToken! });
      setProfile(updated);
      setDisplayName(updated.full_name ?? '');
      setNameSaved(true);
      setTimeout(() => setNameSaved(false), 3000);
    } catch (err) {
      setNameError(err instanceof Error && 'code' in err ? userMessage((err as BeangoError).code) : 'Failed to save display name');
    } finally {
      setSaving(false);
    }
  }, [displayName, idToken]);

  const handleChangeEmail = useCallback(async () => {
    setEmailError(null);
    setEmailMessage(null);

    if (!newEmail.trim()) {
      setEmailError('Please enter a new email address.');
      return;
    }

    if (newEmail.trim() === profile?.email) {
      setEmailError('This is already your current email.');
      return;
    }

    setEmailSending(true);
    try {
      const checkResp = await api.get<{ available: boolean; email: string; message?: string }>(
        `/api/profiles/check-email?email=${encodeURIComponent(newEmail.trim())}`,
        { errorCode: ErrorCode.CHANGE_EMAIL, token: idToken! }
      );
      if (!checkResp.available) {
        setEmailError(checkResp.message ?? 'This email address is already in use by another account.');
        setEmailSending(false);
        return;
      }

      await cognitoAuth.updateEmail(newEmail.trim());
      setEmailMessage('Verification email sent. Check your new inbox to confirm the change.');
      setEmailPending(true);
      setNewEmail('');
    } catch (err) {
      setEmailError(err instanceof Error && 'code' in err ? userMessage((err as BeangoError).code) : 'Failed to update email');
    } finally {
      setEmailSending(false);
    }
  }, [newEmail, profile?.email, idToken]);

  const handleChangePassword = useCallback(async () => {
    setPasswordError(null);

    if (!currentPassword) {
      setPasswordError('Please enter your current password.');
      return;
    }
    if (!newPassword) {
      setPasswordError('Please enter a new password.');
      return;
    }
    if (newPassword === currentPassword) {
      setPasswordError('New password must be different from current password.');
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters and include uppercase, lowercase, and a digit.');
      return;
    }
    if (!/[A-Z]/.test(newPassword)) {
      setPasswordError('Password must include at least one uppercase letter.');
      return;
    }
    if (!/[a-z]/.test(newPassword)) {
      setPasswordError('Password must include at least one lowercase letter.');
      return;
    }
    if (!/[0-9]/.test(newPassword)) {
      setPasswordError('Password must include at least one digit.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('New password and confirmation do not match.');
      return;
    }

    setPasswordSaving(true);
    try {
      await cognitoAuth.changePassword(currentPassword, newPassword);
      await cognitoAuth.globalSignOut();
      void navigate('/login', { state: { message: 'Password changed successfully. Please sign in again.' } });
    } catch (err) {
      setPasswordError(err instanceof Error ? err.message : 'Failed to change password');
    } finally {
      setPasswordSaving(false);
    }
  }, [currentPassword, newPassword, confirmPassword, navigate]);

  const formatDate = (iso?: string) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin text-3xl">🌿</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4">
        <p className="text-destructive">{error}</p>
        <Button variant="outline" onClick={() => void fetchProfile()}>Retry</Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 max-w-2xl">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Profile Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Manage your personal account information and security settings.
        </p>
      </div>

      {/* Display Name */}
      <Card>
        <CardHeader>
          <CardTitle>Display Name</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              value={displayName}
              onChange={(e) => { setDisplayName(e.target.value); setNameError(null); setNameSaved(false); }}
              placeholder="Your display name"
              maxLength={100}
              className="flex-1"
            />
            <Button onClick={() => void handleSaveName()} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
          {nameError && <p className="text-sm text-destructive">{nameError}</p>}
          {nameSaved && <p className="text-sm text-green-600">Display name saved.</p>}
        </CardContent>
      </Card>

      {/* Email */}
      <Card>
        <CardHeader>
          <CardTitle>Email</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="text-sm text-muted-foreground">
            Current email: <span className="text-foreground font-medium">{profile?.email}</span>
          </div>
          {emailPending && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
              Verification pending — check your new email inbox.
            </div>
          )}
          {emailMessage && !emailPending && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
              {emailMessage}
            </div>
          )}
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              value={newEmail}
              onChange={(e) => { setNewEmail(e.target.value); setEmailError(null); }}
              placeholder="New email address"
              type="email"
              className="flex-1"
            />
            <Button onClick={() => void handleChangeEmail()} disabled={emailSending}>
              {emailSending ? 'Checking...' : 'Change Email'}
            </Button>
          </div>
          {emailError && <p className="text-sm text-destructive">{emailError}</p>}
        </CardContent>
      </Card>

      {/* Password */}
      <Card>
        <CardHeader>
          <CardTitle>Change Password</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="relative">
            <Input
              type={showCurrentPassword ? 'text' : 'password'}
              value={currentPassword}
              onChange={(e) => { setCurrentPassword(e.target.value); setPasswordError(null); }}
              placeholder="Current password"
            />
            <button
              type="button"
              onClick={() => setShowCurrentPassword(!showCurrentPassword)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground text-sm"
              aria-label={showCurrentPassword ? 'Hide password' : 'Show password'}
            >
              <i className={`bi ${showCurrentPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
            </button>
          </div>
          <div className="relative">
            <Input
              type={showNewPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => { setNewPassword(e.target.value); setPasswordError(null); }}
              placeholder="New password (min 8 chars, uppercase, lowercase, digit)"
            />
            <button
              type="button"
              onClick={() => setShowNewPassword(!showNewPassword)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground text-sm"
              aria-label={showNewPassword ? 'Hide password' : 'Show password'}
            >
              <i className={`bi ${showNewPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
            </button>
          </div>
          <Input
            type="password"
            value={confirmPassword}
            onChange={(e) => { setConfirmPassword(e.target.value); setPasswordError(null); }}
            placeholder="Confirm new password"
          />
          {passwordError && <p className="text-sm text-destructive">{passwordError}</p>}
          <Button onClick={() => void handleChangePassword()} disabled={passwordSaving}>
            {passwordSaving ? 'Changing...' : 'Change Password'}
          </Button>
          <p className="text-xs text-muted-foreground">
            After changing your password you will be signed out of all devices and asked to sign in again.
          </p>
        </CardContent>
      </Card>

      {/* Account Info */}
      <Card>
        <CardHeader>
          <CardTitle>Account Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Email</span>
            <span className="font-medium">{profile?.email}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Account created</span>
            <span className="font-medium">{formatDate(profile?.created_at)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Last updated</span>
            <span className="font-medium">{formatDate(profile?.updated_at)}</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
